import { Component, type ErrorInfo, type ReactNode } from 'react';
import ServerError from '@/pages/ServerError';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * App-level error boundary (Phase 11). Catches render/runtime errors anywhere in the tree and shows
 * the friendly {@link ServerError} page instead of a blank screen or a raw stack trace. The full
 * error is logged to the console for developers.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Developers get the full detail; users only ever see the friendly page.
    console.error('[Unhandled UI error]', error, info.componentStack);
  }

  private handleReset = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) return <ServerError onRetry={this.handleReset} />;
    return this.props.children;
  }
}
